import { useCallback, useEffect, useRef } from "react";

const DRAFT_PREFIX = "form_draft_";

/**
 * Persist form draft state to sessionStorage so it survives page refreshes.
 * Returns helpers to save, load, and clear the draft.
 */
export function useFormDraft<T>(key: string) {
  const storageKey = DRAFT_PREFIX + key;
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const saveDraft = useCallback(
    (data: T) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        try {
          sessionStorage.setItem(storageKey, JSON.stringify(data));
        } catch {
          // storage full — silently ignore
        }
      }, 500);
    },
    [storageKey]
  );

  const loadDraft = useCallback((): T | null => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  }, [storageKey]);

  const clearDraft = useCallback(() => {
    sessionStorage.removeItem(storageKey);
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, [storageKey]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return { saveDraft, loadDraft, clearDraft };
}
